import { AuthorizationStatus, NotificationStatus } from 'connections-embed-lib';
import { HubspotCertificationIcon } from 'foundations-assets/icons/components/HubspotCertificationIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { bucketInstalls, formatInstalls } from 'marketplace-ui-common/utils/installs';
import { useObserveTruncation } from '../hooks/useObserveTruncation';
import { renderCardButtons } from './internal/CardButtons';
import { ListingBadges } from './internal/ListingBadges';
import { ListingHighlight } from './internal/ListingHighlight';
import { ListingIcon } from './internal/ListingIcon';
import { MarketplaceCardBase } from './internal/MarketplaceCardBase';
import { MarketplaceCardClickable } from './internal/MarketplaceCardClickable';

// https://linear.app/hubspot/issue/MKTD-883
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function formatUsed(value) {
  if (value < 10) {
    return unescapedText('marketplace-embed.card.used.fewerThanMin');
  }
  const abbreviatedValue = I18n.abbreviateNumber(bucketInstalls(value), {
    abbreviateThreshold: 1000
  });
  if (value < 1000) {
    return unescapedText('marketplace-embed.card.used.betweenMinAndThousand', {
      value: abbreviatedValue
    });
  }
  return unescapedText('marketplace-embed.card.used.moreThanMin', {
    value: abbreviatedValue
  });
}
export function MarketplaceCardMedium({
  buttons,
  listing,
  onClick,
  tracker,
  trackingProps
}) {
  var _listing$installCount, _listing$installCount2, _listing$highlights$l, _listing$highlights;
  const product = listing.products[0];
  const cardButtons = renderCardButtons({
    buttons,
    listing
  });
  const hasCardButtons = !!cardButtons.length;
  const {
    isTruncated,
    textRef
  } = useObserveTruncation(listing.listingName);
  const installs = product.productType !== 'SOLUTIONS_PARTNER_PROFILE' && product.productType !== 'WORKFLOW_TEMPLATE' ? formatInstalls((_listing$installCount = listing.installCount) !== null && _listing$installCount !== void 0 ? _listing$installCount : 0) : null;
  return /*#__PURE__*/_jsx(MarketplaceCardBase, {
    componentType: "MarketplaceCardMedium",
    listing: listing,
    tracker: tracker,
    trackingProps: trackingProps,
    children: /*#__PURE__*/_jsxs(CardWrapper, {
      marginBlockEnd: "0",
      blockSize: "100%"
      // For backwards compatibility. Check Sourcegraph before removing.
      ,
      "data-test-id": "ecosystem-app-card",
      children: [/*#__PURE__*/_jsxs(MarketplaceCardClickable, {
        blockSize: "100%",
        componentType: "MarketplaceCardMedium",
        display: "flex",
        flexDirection: "column",
        listing: listing,
        onClick: onClick,
        paddingBlockEnd: hasCardButtons ? undefined : '600',
        paddingBlockStart: "600",
        paddingInline: "600",
        position: "relative",
        trackingProps: trackingProps,
        children: [listing.betaStage && /*#__PURE__*/_jsx(Ribbon, {
          position: "left",
          use: "beta",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-embed.card.beta"
          })
        }), product.installed && product.productId && /*#__PURE__*/_jsx(NotificationStatus, {
          appId: product.productId,
          type: "ribbon"
        }), product.certified && /*#__PURE__*/_jsx(View, {
          insetBlockStart: "600",
          insetInlineEnd: "600",
          padding: "0",
          position: "absolute",
          children: /*#__PURE__*/_jsx(Tooltip, {
            title: unescapedText('marketplace-embed.card.certified'),
            children: /*#__PURE__*/_jsx(HubspotCertificationIcon, {
              "aria-label": unescapedText('marketplace-embed.card.certified'),
              color: "fill-brand-default",
              size: "600"
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          alignSelf: "start",
          children: /*#__PURE__*/_jsx(ListingIcon, {
            listing: listing,
            iconSize: 48
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isTruncated,
          title: listing.listingName,
          children: /*#__PURE__*/_jsx(Text, {
            as: "h4",
            ref: textRef,
            typography: "heading-100",
            marginBlockStart: "400",
            truncate: {
              lineClamp: 2
            },
            children: listing.listingName
          })
        }), /*#__PURE__*/_jsxs(Text, {
          as: "small",
          color: "text-core-subtle",
          marginBlockStart: "100",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "span",
            marginInlineEnd: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-embed.card.providerName",
              options: {
                providerName: listing.companyName
              }
            })
          }), /*#__PURE__*/_jsx(Text, {
            whiteSpace: "nowrap",
            as: "span",
            display: "inline-block",
            children: product.installed && product.productId ? /*#__PURE__*/_jsx(View, {
              marginBlock: "-200",
              children: /*#__PURE__*/_jsx(AuthorizationStatus, {
                appId: product.productId,
                showInstalledStatus: true
              })
            }) : product.productType === 'WORKFLOW_TEMPLATE' ? formatUsed((_listing$installCount2 = listing.installCount) !== null && _listing$installCount2 !== void 0 ? _listing$installCount2 : 0) : installs
          })]
        }), /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "200",
          truncate: {
            lineClamp: 2
          },
          children: listing.description
        }), !hasCardButtons && (((_listing$highlights$l = (_listing$highlights = listing.highlights) === null || _listing$highlights === void 0 ? void 0 : _listing$highlights.length) !== null && _listing$highlights$l !== void 0 ? _listing$highlights$l : 0) > 0 ? /*#__PURE__*/_jsx(ListingHighlight, {
          listing: listing
        }) : /*#__PURE__*/_jsx(ListingBadges, {
          listing: listing
        }))]
      }), hasCardButtons && /*#__PURE__*/_jsx(Flex, {
        marginBlockStart: "auto",
        paddingBlockEnd: "600",
        paddingBlockStart: "400",
        paddingInline: "600",
        children: cardButtons
      })]
    })
  });
}